import React, { useEffect, useState } from "react";
import { STATUS_MAPPING } from "../../../common/Constants";
import { AUDIT_STATUS } from "../../../../utils/auditStateMachine";

const STA_STYLES = `
.sta-backdrop {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.45);
  z-index: var(--z-backdrop);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; pointer-events: none;
  transition: opacity 0.18s ease;
  padding: 16px;
}
.sta-backdrop.open { opacity: 1; pointer-events: auto; }
.sta-modal {
  background: #fff;
  border: 1px solid #E6E6E6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 560px; max-width: 100%;
  max-height: calc(100vh - 32px);
  display: flex; flex-direction: column;
  transform: translateY(8px);
  transition: transform 0.18s ease;
  font-family: 'Montserrat', sans-serif;
  overflow: hidden;
}
.sta-backdrop.open .sta-modal { transform: translateY(0); }
.sta-head {
  display: flex; flex-direction: column; gap: 4px;
  padding: 20px 24px 16px; flex-shrink: 0;
}
.sta-head-row {
  display: flex; align-items: flex-start; justify-content: space-between;
}
.sta-title {
  font-size: 20px; font-weight: 600; color: #1E1E1E;
  font-family: 'Montserrat', sans-serif;
}
.sta-sub {
  font-size: 14px; font-weight: 400; color: #757575;
  font-family: 'Montserrat', sans-serif;
}
.sta-close {
  background: none; border: none; cursor: pointer;
  padding: 4px; color: #1E1E1E;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.sta-close:hover { opacity: 0.7; }
.sta-close svg { width: 16px; height: 16px; display: block; }
.sta-divider { height: 1px; background: #E6E6E6; flex-shrink: 0; }
.sta-body {
  padding: 20px 24px;
  display: flex; flex-direction: column; gap: 16px;
  overflow-y: auto; flex: 1;
}
.sta-summary {
  font-size: 14px; font-weight: 400; color: #1E1E1E;
  font-family: 'Montserrat', sans-serif;
  margin: 0;
}
.sta-summary strong { font-weight: 600; }
.sta-counts { display: flex; flex-direction: column; gap: 8px; }
.sta-count-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 4px;
}
.sta-pill {
  display: inline-block; padding: 3px 10px; border-radius: 9999px;
  font-size: 13px; font-weight: 700;
  font-family: 'Montserrat', sans-serif; white-space: nowrap;
}
.sta-count-num {
  font-size: 14px; font-weight: 600; color: #1E1E1E;
  font-family: 'Montserrat', sans-serif;
}
.sta-warning {
  font-size: 14px; font-weight: 400; color: #757575;
  font-family: 'Montserrat', sans-serif;
  margin: 0;
}
.sta-foot {
  display: flex; gap: 16px;
  padding: 16px 24px 20px; flex-shrink: 0;
}
.sta-cancel, .sta-confirm {
  flex: 1; height: 42px; border-radius: 8px; border: none;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px; font-weight: 500;
  cursor: pointer;
}
.sta-cancel { background: #F3F4F6; color: #0F0F0F; }
.sta-cancel:hover { background: #E5E7EB; }
.sta-confirm { background: #2965AD; color: #fff; }
.sta-confirm:hover { background: #1e4f8a; }
`;

const STATUS_PILL_STYLES: Record<string, { background: string; color: string }> = {
  ActivitySaved: { background: "rgba(44,44,44,0.1)",    color: "#2c2c2c" },
  Pending:       { background: "rgba(220,53,69,0.1)",   color: "#dc3545" },
  Overdue:       { background: "rgba(237,138,52,0.1)",  color: "#ed8a34" },
  Submitted:     { background: "rgba(20,174,92,0.1)",   color: "#14ae5c" },
  Rejected:      { background: "rgba(151,81,2,0.1)",    color: "#975102" },
  Approved:      { background: "rgba(41,101,173,0.1)",  color: "#2965ad" },
};

interface SubmitToAuditorModalV2Props {
  onClose: () => void;
  onSubmit: (e: any) => void;
  selectedRows: any[];
  auditStatus: string;
}

function SubmitToAuditorModalV2({ onClose, onSubmit, selectedRows, auditStatus }: SubmitToAuditorModalV2Props) {
  const [counts, setCounts] = useState<any[]>([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (selectedRows) {
      const _counts: any = {};
      selectedRows.forEach((row: any) => {
        if (!row.published) {
          _counts[row.status] = (_counts[row.status] || 0) + 1;
        }
      });
      setCounts(
        Object.keys(_counts).map((key) => ({
          key,
          label: STATUS_MAPPING[key],
          count: _counts[key],
        }))
      );
    }
  }, [selectedRows]);

  const isFinalAuditEmpty =
    selectedRows.length === 0 &&
    ([AUDIT_STATUS.FINAL_AUDIT_PENDING] as string[]).includes(auditStatus);

  return (
    <>
      <style>{STA_STYLES}</style>
      <div className={"sta-backdrop" + (visible ? " open" : "")}>
        <div className="sta-modal" role="dialog" aria-modal="true">
          <div className="sta-head">
            <div className="sta-head-row">
              <span className="sta-title">Submit to Auditor</span>
              <button className="sta-close" onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <span className="sta-sub">Review activities before submitting to the auditor.</span>
          </div>

          <div className="sta-divider" />

          <div className="sta-body">
            {isFinalAuditEmpty ? (
              <p className="sta-summary">All the activities will be submitted for final audit.</p>
            ) : (
              <>
                <p className="sta-summary">
                  There are{" "}
                  <strong>
                    {selectedRows.length}{" "}
                    {selectedRows.length === 1 ? "activity" : "activities"}
                  </strong>{" "}
                  available for submission.
                </p>
                {counts.length > 0 && (
                  <div className="sta-counts">
                    {counts.map((c) => {
                      const s = STATUS_PILL_STYLES[c.key] || {
                        background: "rgba(0,0,0,0.05)",
                        color: "#1E1E1E",
                      };
                      return (
                        <div className="sta-count-row" key={c.key}>
                          <span
                            className="sta-pill"
                            style={{ background: s.background, color: s.color }}
                          >
                            {c.label || c.key}
                          </span>
                          <span className="sta-count-num">{c.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            <p className="sta-warning">
              Upon submission, you will not be able to edit these activities further.
            </p>
          </div>

          <div className="sta-divider" />

          <div className="sta-foot">
            <button className="sta-cancel" onClick={onClose}>No</button>
            <button className="sta-confirm" onClick={onSubmit}>Yes</button>
          </div>
        </div>
      </div>
    </>
  );
}

export default SubmitToAuditorModalV2;
